// src/components/FD/CloseAccount.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import '../GL/LoanPayment.css';

const CloseAccount = () => {
  const [applications, setApplications] = useState([]);
  const [closedAccounts, setClosedAccounts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState({});
  const [showClosed, setShowClosed] = useState(false);

  useEffect(() => {
    fetchApplications();
    fetchClosedAccounts();
  }, []);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/fd/applications-for-payment`);
      setApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      alert('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchClosedAccounts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/fd/closed-accounts`);
      setClosedAccounts(response.data);
    } catch (error) {
      console.error('Error fetching closed accounts:', error);
    }
  };

  const handleClose = async (registrationNo, name) => {
    if (!window.confirm(`Are you sure you want to close the account for ${name} (${registrationNo})? This will remove it from transaction history.`)) {
      return;
    }

    try {
      setClosing(prev => ({ ...prev, [registrationNo]: true }));
      
      await axios.post(`${API_BASE_URL}/api/fd/close-account`, {
        registration_no: registrationNo
      });

      alert('Account closed successfully!');
      
      // Refresh lists
      fetchApplications();
      fetchClosedAccounts();
    } catch (error) {
      console.error('Error closing account:', error);
      alert(error.response?.data?.error || 'Failed to close account');
    } finally {
      setClosing(prev => ({ ...prev, [registrationNo]: false }));
    }
  };

  const filteredApplications = searchValue.trim()
    ? applications.filter(app => 
        app.registration_no.toLowerCase().includes(searchValue.toLowerCase()) ||
        app.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        app.phone_no.includes(searchValue)
      )
    : applications;

  const filteredClosedAccounts = searchValue.trim()
    ? closedAccounts.filter(acc => 
        acc.registration_no.toLowerCase().includes(searchValue.toLowerCase()) ||
        acc.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        acc.phone_no.includes(searchValue)
      )
    : closedAccounts;

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading applications...</div>;
  }

  return (
    <div className="loan-payment-container">
      <div className="loan-payment-form">
        <h1>Close Account</h1>
        
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search by registration no, name, or phone number"
            style={{
              flex: '1',
              minWidth: '200px',
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '6px',
              fontSize: '16px'
            }}
          />
          <button
            onClick={() => setShowClosed(!showClosed)}
            style={{
              padding: '8px 16px',
              backgroundColor: showClosed ? '#6c757d' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            {showClosed ? 'Show Active Accounts' : 'Show Closed Accounts'}
          </button>
        </div>

        {!showClosed ? (
          <>
            <h2 style={{ marginBottom: '15px' }}>Active Accounts</h2>
            {filteredApplications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No active applications found.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Registration No</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Phone No</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApplications.map((app) => (
                      <tr key={app.registration_no}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.registration_no}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.name}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{app.phone_no}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                          <button
                            onClick={() => handleClose(app.registration_no, app.name)}
                            disabled={closing[app.registration_no]}
                            style={{
                              padding: '8px 16px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: closing[app.registration_no] ? 'not-allowed' : 'pointer',
                              opacity: closing[app.registration_no] ? 0.6 : 1
                            }}
                          >
                            {closing[app.registration_no] ? 'Closing...' : 'Close Account'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <h2 style={{ marginBottom: '15px' }}>Closed Accounts</h2>
            {filteredClosedAccounts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>No closed accounts found.</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto', marginTop: '20px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Registration No</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Name</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Phone No</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Closed Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredClosedAccounts.map((acc) => (
                      <tr key={acc.registration_no}>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{acc.registration_no}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{acc.name}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd' }}>{acc.phone_no}</td>
                        <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>
                          {formatDateToDDMMYYYY(acc.closed_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CloseAccount;

